/**
 * Custom useMarketData Hook
 * Provides market data connection state management - follows same pattern as useOAuth
 * TASK-086: Updated to use httpClient for gateway enforcement
 */

import { useState, useEffect, useCallback } from 'react';
import { httpClient } from '../lib/httpClient';

export interface MarketDataConfig {
  apiKey: string;
  planTier: 'free' | 'basic' | 'premium';
  connectedAt?: string;
}

export interface MarketDataStatus {
  connected: boolean;
  config?: MarketDataConfig;
  last_sync?: string;
  quotesThisHour?: number;
  rateLimitRemaining?: number;
}

export interface UseMarketDataReturn {
  // State
  isConnected: boolean;
  config: MarketDataConfig | null;
  status: MarketDataStatus | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  connect: (config: MarketDataConfig) => Promise<void>;
  disconnect: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  clearError: () => void;

  // Connection flow helpers
  isConnecting: boolean;
}

export const useMarketData = (): UseMarketDataReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [config, setConfig] = useState<MarketDataConfig | null>(null);
  const [status, setStatus] = useState<MarketDataStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch market data status from backend - fallback to config endpoint until /status is implemented
  // TASK-086: Uses httpClient for gateway enforcement
  const refreshStatus = useCallback(async () => {
    try {
      setError(null);

      console.log('Refreshing market data status...');

      // Try the status endpoint first (preferred)
      try {
        const statusData = await httpClient.get<MarketDataStatus>('/market/status');
        console.log('Market data status received:', statusData);

        setStatus(statusData);
        setIsConnected(statusData.connected);
        setConfig(statusData.config || null);
        return;
      } catch (statusError: any) {
        // Status endpoint not implemented yet, fallback to config endpoint
        if (statusError?.status === 404) {
          console.log('Status endpoint not found, checking config endpoint...');

          try {
            const configData = await httpClient.get<any>('/market/config');
            console.log('Market data config received:', configData);

            // Transform config response to status format
            const isConnected = configData.configured && configData.enabled !== false;
            const statusData: MarketDataStatus = {
              connected: isConnected,
              config: isConnected ? {
                apiKey: configData.api_key || '',
                planTier: configData.plan_tier || 'free',
                connectedAt: configData.updated_at
              } : undefined
            };

            setStatus(statusData);
            setIsConnected(isConnected);
            setConfig(statusData.config || null);
            return;
          } catch (configError: any) {
            if (configError?.status === 404) {
              // No market data configured
              setStatus({ connected: false });
              setIsConnected(false);
              setConfig(null);
              setIsLoading(false);
              return;
            }
            throw configError;
          }
        }
        throw statusError;
      }

    } catch (error) {
      console.error('Error refreshing market data status:', error);
      setError(error instanceof Error ? error.message : 'Failed to check market data status');
      setStatus({ connected: false });
      setIsConnected(false);
      setConfig(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Connect to market data provider
  // TASK-086: Uses httpClient for gateway enforcement
  const connect = useCallback(async (marketConfig: MarketDataConfig) => {
    try {
      setIsConnecting(true);
      setError(null);

      console.log('Connecting to market data with config:', { ...marketConfig, apiKey: '****' });

      // Test the API key first
      await httpClient.post('/market/test-connection', { apiKey: marketConfig.apiKey });

      // Save the configuration
      await httpClient.post('/market/config', marketConfig);

      // Refresh status to get updated state
      await refreshStatus();

    } catch (error: any) {
      console.error('Market data connection failed:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Connection failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsConnecting(false);
    }
  }, [refreshStatus]);

  // Disconnect from market data provider
  // TASK-086: Uses httpClient for gateway enforcement
  const disconnect = useCallback(async () => {
    try {
      setError(null);

      await httpClient.post('/market/disconnect');

      // Update local state immediately
      setStatus({ connected: false });
      setIsConnected(false);
      setConfig(null);

    } catch (error: any) {
      console.error('Market data disconnect failed:', error);
      setError(error instanceof Error ? error.message : 'Disconnect failed');
    }
  }, []);

  // Initialize hook - check status on mount (same pattern as OAuth)
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  return {
    // State
    isConnected,
    config,
    status,
    isLoading,
    error,

    // Actions
    connect,
    disconnect,
    refreshStatus,
    clearError,

    // Connection flow helpers
    isConnecting
  };
};